import type { ReactElement, Ref } from 'react';

import { Text, Tooltip } from '@frontend/components';

import { AssemblyBadge } from './AssemblyBadge';
import { DeletedThumbnail } from './DeletedThumbnail';
import { HasVirusThumbnail } from './HasVirusThumbnail';
import { LoadingThumbnail } from './LoadingThumbnail';
import { PartThumbnailFallback } from './PartThumbnailFallback';
import { ProcessingFailureBadge } from './ProcessingFailureBadge';
import { ThumbnailContainer } from './ThumbnailContainer';
import type { ThumbnailContainerProps } from './ThumbnailContainer';
import { ThumbnailImage } from './ThumbnailImage';
import { useFileType } from './hooks';

export const failedPartLanguage = {
  headline: "Couldn't process file",
  description: 'This part may have missing or invalid data.',
};

export const failedChildrenLanguage = {
  headline: "Some child part files couldn't be processed",
  description:
    "These parts may have invalid data. They don't appear in the thumbnail.",
};

export type PartThumbnailFromUrlProps = ThumbnailContainerProps & {
  isFetching?: boolean;
  thumbnailUrl?: string | null;
  containerRef?: Ref<HTMLDivElement>;
  containsVirus?: boolean;
  isViewerDataPurged?: boolean;
  filename?: string | null;
  isAssembly?: boolean;
  fallbackComponent?: ReactElement | null;
  loadingComponent?: ReactElement | null;
  fileType?: string | null;
  isSearchTermMatch?: boolean;
  hasFailedChildren?: boolean;
  processingFailed?: boolean;
  showTooltips?: boolean;
  badgeSize?: 'sm' | 'md';
};

/**
 * Renders a part thumbnail from props including the thumbnail image URL.
 *
 * In general, it is preferred to use the PartThumbnailDataWrapper component,
 * which fetches the thumbnail image based on the part file UUID from an
 * endpoint into which we are building permissions to ensure that the user
 * should be allowed to view the thumbnail, which facilitates FedRAMP
 * compliance (thumbnail images are considered CUI). There likely are valid use
 * cases for using this component directly, but do so with caution, and in a way
 * which adequately protects CUI.
 */
export const PartThumbnailFromUrl = ({
  isFetching,
  thumbnailUrl,
  containerRef,
  fileType,
  filename,
  containsVirus,
  isViewerDataPurged,
  isBoxShadowVisible,
  isBorderRadius,
  isSearchTermMatch,
  loadingComponent = null,
  fallbackComponent = null,
  isAssembly,
  hasFailedChildren,
  processingFailed,
  showTooltips,
  badgeSize = 'sm',
  ...restProps
}: PartThumbnailFromUrlProps) => {
  const { isPdf } = useFileType({ fileType, filename });

  return (
    <ThumbnailContainer
      ref={containerRef}
      isBoxShadowVisible={isBoxShadowVisible}
      isBorderRadius={isBorderRadius}
      borderColor={
        isSearchTermMatch ? '#FF771A' : processingFailed ? 'alert.error' : ''
      }
      borderWidth="2px"
      {...restProps}
    >
      {isViewerDataPurged ? (
        <DeletedThumbnail />
      ) : containsVirus ? (
        <HasVirusThumbnail />
      ) : (
        <>
          {isFetching ? (
            loadingComponent || <LoadingThumbnail />
          ) : thumbnailUrl && !processingFailed ? (
            <ThumbnailImage src={thumbnailUrl} alt={filename} />
          ) : (
            fallbackComponent || <PartThumbnailFallback isPdf={isPdf} />
          )}
        </>
      )}
      {isAssembly &&
        (showTooltips ? (
          <Tooltip label="Part is assembly">
            <AssemblyBadge size={badgeSize} />
          </Tooltip>
        ) : (
          <AssemblyBadge size={badgeSize} />
        ))}
      {(processingFailed || hasFailedChildren) &&
        (showTooltips ? (
          <Tooltip
            label={
              <>
                <Text fontWeight="bold" color="feature.onInteraction">
                  {processingFailed
                    ? failedPartLanguage.headline
                    : failedChildrenLanguage.headline}
                </Text>
                <Text color="feature.onInteraction" variant="expanded">
                  {processingFailed
                    ? failedPartLanguage.description
                    : failedChildrenLanguage.description}
                </Text>
              </>
            }
          >
            <ProcessingFailureBadge size={badgeSize} />
          </Tooltip>
        ) : (
          <ProcessingFailureBadge size={badgeSize} />
        ))}
    </ThumbnailContainer>
  );
};
